import { useMemo, useState } from "react";
import { View, StyleSheet } from "react-native";
import { NavigationProp, useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { ScrollView } from "react-native-gesture-handler";
import { Menu } from "react-native-paper";
import { PanelLeft, PanelLeftClose } from "lucide-react";
import {
  BooksIcon,
  SquaresFourIcon,
  ChalkboardTeacherIcon,
  CertificateIcon,
  GraduationCapIcon,
  UsersIcon,
  ClipboardTextIcon,
  ChartLineIcon,
  GearIcon,
  UserIcon,
  SignOutIcon,
  MegaphoneIcon,
  FirstAidKitIcon,
  FileMagnifyingGlassIcon,
  BellRingingIcon,
} from "@phosphor-icons/react";
import { Plug } from "lucide-react";

import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useAuth } from "../hooks/useAuth";
import { useVisitSession } from "../contexts/VisitSessionContext";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import UserInitial from "../components/general/UserInitial/UserInitial";
import AdminBreadcrumbs from "../components/adminDashboard/AdminBreadcrumbs";
import type { RootStackParamList } from "../navigation/navigation.types";
import AxioLogo from "../components/svgs/logos/AxioLogo";
import { hasMinRole, UserRoleUI } from "@axio/auth-guard";
import { NotificationIcon } from "../components/general/NotificationIcon/NotificationIcon";
import { useNotification } from "../contexts/NotificationContext";
import { useAppConfig } from "../AppConfigProvider";
import Tooltip from "../components/common/tooltip/Tooltip";
import { colors, spacing } from "../styles/design-system";

type MenuItem = {
  name: string;
  label: string;
  navigateTo: keyof RootStackParamList;
  icon: React.FC<{ size: number; color: string }>;
  minRole: UserRoleUI;
};

const DashboardIcon = ({ size = 20, color = "#667085" }) => <SquaresFourIcon color={color} size={size} />;
const ManageCoursesIcon = ({ size = 20, color = "#667085" }) => <BooksIcon color={color} size={size} />;
const TeachingIcon = ({ size = 20, color = "#667085" }) => <ChalkboardTeacherIcon color={color} size={size} />;
const DegreesProgramsIcon = ({ size = 20, color = "#667085" }) => <CertificateIcon color={color} size={size} />;
const StudentsIcon = ({ size = 20, color = "#667085" }) => <GraduationCapIcon color={color} size={size} />;
const MultipleUsersIcon = ({ size = 20, color = "#667085" }) => <UsersIcon color={color} size={size} />;
const GradingCenterIcon = ({ size = 20, color = "#667085" }) => <ClipboardTextIcon color={color} size={size} />;
const AnnouncementsIcon = ({ size = 20, color = "#667085" }) => <MegaphoneIcon color={color} size={size} />;
const AnalyticsIcon = ({ size = 20, color = "#667085" }) => <ChartLineIcon color={color} size={size} />;
const SettingsIcon = ({ size = 20, color = "#667085" }) => <GearIcon color={color} size={size} />;
const PluginIcon = ({ size = 20, color = "#667085" }) => <Plug color={color} size={size} />;
const SafetySupportIcon = ({ size = 20, color = "#667085" }) => <FirstAidKitIcon color={color} size={size} />;
const CourseModernizerIcon = ({ size = 20, color = "#667085" }) => (
  <FileMagnifyingGlassIcon color={color} size={size} />
);
const NudgeManagementIcon = ({ size = 20, color = "#667085" }) => <BellRingingIcon color={color} size={size} />;

const courseModernizerMenuItem: MenuItem = {
  name: "Course Modernizer",
  label: "Course Modernizer",
  navigateTo: "Course Modernizer",
  icon: CourseModernizerIcon,
  minRole: UserRoleUI.FACULTY,
};

const aiCompanionMenuItems: MenuItem[] = [
  {
    name: "Admin Companion Dashboard",
    label: "Dashboard",
    navigateTo: "Admin Companion Dashboard",
    icon: DashboardIcon,
    minRole: UserRoleUI.AUDITOR,
  },
  {
    name: "Faculty Companion Dashboard",
    label: "Dashboard",
    navigateTo: "Faculty Companion Dashboard",
    icon: DashboardIcon,
    minRole: UserRoleUI.FACULTY,
  },
  {
    name: "Safety Support",
    label: "Safety & Support",
    navigateTo: "Safety Support",
    icon: SafetySupportIcon,
    minRole: UserRoleUI.ADMIN,
  },
  // Temporarily hidden — re-enable when Nudge Management is ready
  // {
  //   name: "Nudge Management",
  //   label: "Nudge Management",
  //   navigateTo: "Nudge Management",
  //   icon: NudgeManagementIcon,
  //   minRole: UserRoleUI.FACULTY,
  // },
  {
    name: "Admin Settings",
    label: "Settings",
    navigateTo: "Admin Settings",
    icon: SettingsIcon,
    minRole: UserRoleUI.AUDITOR,
  },
];

const aiLearnMenuItems: MenuItem[] = [
  {
    name: "Admin Dashboard",
    label: "Dashboard",
    navigateTo: "Admin Dashboard",
    icon: DashboardIcon,
    minRole: UserRoleUI.FACULTY,
  },
  {
    name: "Manage Courses",
    label: "Manage Courses",
    navigateTo: "Manage Courses",
    icon: ManageCoursesIcon,
    minRole: UserRoleUI.FACULTY,
  },
  { name: "Teaching", label: "Teaching", navigateTo: "Teaching", icon: TeachingIcon, minRole: UserRoleUI.FACULTY },
  {
    name: "Degrees & Programs",
    label: "Degrees & Programs",
    navigateTo: "Degrees & Programs",
    icon: DegreesProgramsIcon,
    minRole: UserRoleUI.FACULTY,
  },
  {
    name: "Students",
    label: "Students",
    navigateTo: "Students",
    icon: StudentsIcon,
    minRole: UserRoleUI.FACULTY,
  },
  {
    name: "Admin Users",
    label: "Users",
    navigateTo: "Admin Users",
    icon: MultipleUsersIcon,
    minRole: UserRoleUI.AUDITOR,
  },
  {
    name: "Admin Grading Center",
    label: "Grading Center",
    navigateTo: "Admin Grading Center",
    icon: GradingCenterIcon,
    minRole: UserRoleUI.FACULTY,
  },
  {
    name: "Admin Announcements",
    label: "Announcements",
    navigateTo: "Admin Announcements",
    icon: AnnouncementsIcon,
    minRole: UserRoleUI.ADMIN,
  },
  {
    name: "Admin Analytics",
    label: "Analytics",
    navigateTo: "Admin Analytics",
    icon: AnalyticsIcon,
    minRole: UserRoleUI.FACULTY,
  },
  // Temporarily hidden — re-enable when Nudge Management is ready
  // {
  //   name: "Nudge Management",
  //   label: "Nudge Management",
  //   navigateTo: "Nudge Management",
  //   icon: NudgeManagementIcon,
  //   minRole: UserRoleUI.FACULTY,
  // },
  // {
  //   name: "Admin Integrations",
  //   label: "Integrations",
  //   navigateTo: "Admin Integrations",
  //   icon: PluginIcon,
  //   minRole: UserRoleUI.ADMIN,
  // },
  {
    name: "Admin Settings",
    label: "Settings",
    navigateTo: "Admin Settings",
    icon: SettingsIcon,
    minRole: UserRoleUI.AUDITOR,
  },
];

type AdminLayoutProps = {
  children: React.ReactNode;
  withPadding?: boolean;
  isCourseModernizerPage?: boolean;
};

function AdminLayout({ children, withPadding = true, isCourseModernizerPage = false }: AdminLayoutProps) {
  const { isMedium } = useResponsiveScreen();
  const { isCompanionMode } = useAppConfig();
  const { logout, userInfo } = useAuth();
  const { endSession } = useVisitSession();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList>>();

  const { toggleNotifications } = useNotification();
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
  const [userMenuVisible, setUserMenuVisible] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarIconHovered, setIsSidebarIconHovered] = useState(false);

  const currentPageName = route.name;

  // Determine user role and filter menu items
  const menuItems = useMemo(() => {
    if (isCompanionMode) {
      if (isCourseModernizerPage) {
        return hasMinRole(userInfo.userRole, courseModernizerMenuItem.minRole) ? [courseModernizerMenuItem] : [];
      }

      return aiCompanionMenuItems
        .filter((item) => hasMinRole(userInfo.userRole, item.minRole))
        .filter((item) => {
          if (item.name === "Faculty Companion Dashboard") {
            return userInfo.userRole === UserRoleUI.FACULTY;
          }
          return true;
        });
    }

    return aiLearnMenuItems.filter((item) => hasMinRole(userInfo.userRole, item.minRole));
  }, [isCompanionMode, isCourseModernizerPage, userInfo.userRole]);

  const handleMenuItemPress = (item: MenuItem) => {
    navigation.navigate(item.navigateTo as never);
  };

  const handleViewProfile = () => {
    setUserMenuVisible(false);
    navigation.navigate("Admin Profile");
  };

  const handleLogout = async () => {
    setUserMenuVisible(false);
    await endSession();
    logout();
  };

  if (!isMedium) {
    return (
      <View style={{ flex: 1, backgroundColor: "#fff" }}>
        <CustomText
          text="Mobile admin view coming soon"
          size="l"
          useTranslationText={false}
          style={{ textAlign: "center", marginTop: 50 }}
        />
      </View>
    );
  }

  if (userInfo.userRole === UserRoleUI.STUDENT) {
    navigation.navigate(isCompanionMode ? "Companion Home" : "Home");
  }

  return (
    <View style={styles.container}>
      <View style={styles.body}>
        {/* Sidebar with Logo */}
        <View style={[styles.sidebar, isSidebarCollapsed && styles.sidebarCollapsed]}>
          {/* Logo Section */}
          <View style={styles.logoSection}>
            <AxioLogo height={28} />
            <Tooltip
              content={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement="right"
              horizontalOffset={5}
              showArrow={false}>
              <CustomButton
                styleType="secondaryLightened"
                size="s"
                style={styles.sidebarIcon}
                leftIcon={
                  isSidebarCollapsed ? (
                    <PanelLeft size={20} color={isSidebarIconHovered ? colors.grey[700] : colors.grey[500]} />
                  ) : (
                    <PanelLeftClose size={20} color={isSidebarIconHovered ? colors.grey[700] : colors.grey[500]} />
                  )
                }
                onHover={setIsSidebarIconHovered}
                onPress={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                accessibilityLabel="Toggle sidebar"
                accessibilityHint="Toggle the sidebar visibility"
              />
            </Tooltip>
          </View>

          {/* Menu Items */}
          <View style={[styles.sidebarContent, isSidebarCollapsed && styles.sidebarContentCollapsed]}>
            {menuItems.map((item, index) => {
              const isActive = item.name === currentPageName;
              const isHovered = hoveredMenuItem === item.name;
              const iconColor = isActive ? "#012298" : isHovered ? "#344054" : "#667085";

              return (
                <CustomButton
                  key={index}
                  text={isSidebarCollapsed ? null : item.label}
                  leftIcon={<item.icon size={20} color={iconColor} />}
                  onPress={() => handleMenuItemPress(item)}
                  styleType="transparent"
                  size="m"
                  useTranslationText={false}
                  onHoverIn={() => setHoveredMenuItem(item.name)}
                  onHoverOut={() => setHoveredMenuItem(null)}
                  style={[
                    styles.menuItem,
                    isSidebarCollapsed && styles.menuItemCollapsed,
                    isActive && styles.menuItemActive,
                    !isActive && isHovered && styles.menuItemHovered,
                  ]}
                  textStyle={[
                    styles.menuItemText,
                    isActive && styles.menuItemTextActive,
                    !isActive && isHovered && styles.menuItemTextHovered,
                  ]}
                />
              );
            })}
          </View>
        </View>

        {/* Main content area with header */}
        <View style={styles.mainContent}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <AdminBreadcrumbs currentRoute={currentPageName} />
            </View>

            <View style={styles.headerRight}>
              {!isCourseModernizerPage && (
                <CustomButton
                  onPress={toggleNotifications}
                  text={null}
                  leftIcon={<NotificationIcon />}
                  size="m"
                  styleType="transparent"
                  style={styles.headerButton}
                  aria-label="Notifications"
                />
              )}

              <Menu
                visible={userMenuVisible}
                onDismiss={() => setUserMenuVisible(false)}
                anchor={
                  <CustomButton
                    onPress={() => setUserMenuVisible(true)}
                    text={null}
                    leftIcon={<UserInitial initial={userInfo.userInitial} type="menu" />}
                    size="m"
                    styleType="transparent"
                    style={styles.headerButton}
                    aria-label="User menu"
                  />
                }
                contentStyle={styles.menuContent}>
                <Menu.Item
                  onPress={handleViewProfile}
                  title="View Profile"
                  leadingIcon={() => <UserIcon size={16} color="#101828" />}
                  titleStyle={styles.menuItemTitle}
                />
                <Menu.Item
                  onPress={handleLogout}
                  title="Log Out"
                  leadingIcon={() => <SignOutIcon size={16} color="#DC2626" />}
                  titleStyle={[styles.menuItemTitle, { color: "#DC2626" }]}
                />
              </Menu>
            </View>
          </View>

          {/* Content */}
          <View style={styles.content}>
            <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
              <View style={withPadding ? styles.contentPadding : styles.contentNoPadding}>{children}</View>
            </ScrollView>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  body: {
    flex: 1,
    flexDirection: "row",
  },
  sidebar: {
    width: 240,
    backgroundColor: "#FCFCFD",
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
    flexDirection: "column",
  },
  sidebarCollapsed: {
    width: 67,
  },
  logoSection: {
    height: 57,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.white,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sidebarIcon: {
    borderRadius: "50%",
  },
  sidebarContent: {
    gap: 4,
    marginTop: 32,
    flex: 1,
    paddingHorizontal: 12,
  },
  sidebarContentCollapsed: {
    alignItems: "center",
    paddingHorizontal: 8,
  },
  menuItem: {
    justifyContent: "flex-start",
    borderRadius: 16,
    width: "100%",
  },
  menuItemCollapsed: {
    justifyContent: "center",
    width: 48,
    height: 48,
    minWidth: 48,
    paddingHorizontal: 0,
  },
  menuItemActive: {
    backgroundColor: "#E6EBFF",
  },
  menuItemHovered: {
    backgroundColor: "#F2F4F7",
  },
  menuItemText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "500",
  },
  menuItemTextActive: {
    color: "#012298",
    fontWeight: "500",
  },
  menuItemTextHovered: {
    color: "#344054",
    fontWeight: "500",
  },
  mainContent: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: "#F2F4F7",
  },
  header: {
    height: 72,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    zIndex: 10,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 4,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerButton: {
    padding: 8,
  },
  content: {
    flex: 1,
  },
  scrollView: {
    height: 20,
  },
  contentPadding: {
    padding: 32,
    width: "100%",
    marginHorizontal: "auto",
  },
  contentNoPadding: {
    width: "100%",
    marginHorizontal: "auto",
  },
  menuContent: {
    backgroundColor: "#fff",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
  menuItemTitle: {
    fontSize: 12,
    fontWeight: "500",
    color: "#101828",
  },
});

export default AdminLayout;
